
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useSubscription } from "@/hooks/use-subscription"

export function CreateOrganizationDialog({ onOrganizationCreated }: { onOrganizationCreated: () => void }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [organizationName, setOrganizationName] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { plan } = useSubscription()

  const handleCreateOrganization = async () => {
    if (!organizationName.trim()) {
      setError(t("common.organizations.nameRequired"))
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        toast.error(t("common.auth.noUser"))
        return
      }

      // Create organization
      const { data: organization, error: orgError } = await supabase
        .from("organizations")
        .insert([{ 
          name: organizationName.trim()
        }])
        .select()
        .single()

      if (orgError) {
        console.error("Error creating organization:", orgError)
        if (orgError.message.includes("check_organization_limit")) {
          setError(t("common.organizations.limitReached"))
          toast.error(t("common.organizations.limitReached"))
        } else {
          setError(t("common.organizations.errorCreating"))
          toast.error(t("common.organizations.errorCreating"))
        }
        return
      }

      if (!organization) {
        throw new Error("No organization returned after creation")
      }

      // Add the current user as an owner of the organization
      const { error: memberError } = await supabase
        .from("organization_members")
        .insert([{
          organization_id: organization.id,
          user_id: user.id,
          role: 'owner'
        }])

      if (memberError) {
        console.error("Error adding member:", memberError)
        // Try to cleanup the organization if member creation fails
        await supabase
          .from("organizations")
          .delete()
          .eq('id', organization.id)
        throw memberError
      }

      toast.success(t("common.organizations.created"))
      setOpen(false)
      setOrganizationName("")
      onOrganizationCreated()
    } catch (error: any) {
      console.error("Error in organization creation:", error)
      setError(t("common.organizations.errorCreating"))
      toast.error(t("common.organizations.errorCreating"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          {t("common.organizations.create")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.organizations.createNew")}</DialogTitle>
          <DialogDescription>
            {t("common.organizations.createDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="name">{t("common.organizations.name")}</Label>
            <Input
              id="name"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder={t("common.organizations.namePlaceholder")}
              disabled={isLoading}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="submit"
            onClick={handleCreateOrganization}
            disabled={isLoading || !organizationName.trim()}
          >
            {isLoading ? t("common.loading") : t("common.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
